import { ControlledInputProps, SimplePrimitive } from "@vixi/types";
import { CheckIcon, ChevronsUpDownIcon, Loader2, XIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/base/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/base/command";
import { textInputVariants } from "../input/text/styling";
import { ComponentProps, ReactNode, useMemo, useState } from "react";
import { PopoverContentProps } from "@radix-ui/react-popover";
import { Button } from "./button";
import { CommandLoading } from "cmdk";

function defaultGetKeyByValue<TValue = SimplePrimitive>(value: TValue): string | number {
  return `${value}`;
}

export interface AutocompleteProps<TValue = SimplePrimitive>
  extends Partial<ControlledInputProps<TValue>> {
  options: TValue[];
  value?: TValue | null;
  getOptionLabel?: (value: TValue) => ReactNode;
  getOptionKey?: (value: TValue) => string | number;
  getOptionByKey?: (key: string | number) => TValue | null;
  noOptionsMessage?: ReactNode;
  placeholder?: ReactNode;
  searchPlaceholder?: string;
  isNullable?: boolean;
  onSearch?: (query: string) => void;
  afterOpenChange?: (open: boolean) => void;
  loading?: boolean;
  slotProps?: {
    buttonProps?: ComponentProps<"button">;
    popoverContent?: PopoverContentProps;
  };
}

export const Autocomplete = <TValue = SimplePrimitive,>({
  options,
  getOptionKey: getOptionKey = defaultGetKeyByValue,
  getOptionByKey: getOptionByKeyProp,
  getOptionLabel = (value) => `${value}`,
  value,
  onChange,
  noOptionsMessage,
  onBlur,
  slotProps,
  placeholder,
  searchPlaceholder,
  onSearch,
  loading,
  afterOpenChange,
  isNullable = false,
}: AutocompleteProps<TValue>) => {
  const [open, _setOpen] = useState(false);

  const setOpen = (value: boolean) => {
    _setOpen(value);
    if (!value) onBlur?.();
    afterOpenChange?.(value);
  };

  const mappedOptions = useMemo(
    () =>
      options.map((option) => ({
        key: `${getOptionKey(option)}`,
        label: getOptionLabel(option),
      })),
    [options],
  );

  const getOptionByKey =
    getOptionByKeyProp ?? ((key) => options.find((value) => getOptionKey(value) === key) ?? null);

  const selected = useMemo(() => {
    if (!value) return null;

    const key = getOptionKey(value);
    const label = getOptionLabel(value);
    const option = getOptionByKey(key);

    if (option) return { ...option, key, label };

    return null;
  }, [value, getOptionByKey]);

  return (
    <div className="relative h-fit w-fit">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            {...slotProps?.buttonProps}
            role="combobox"
            aria-expanded={open}
            className={cn(
              textInputVariants({
                focused: open,
              }),
              "flex cursor-pointer items-center justify-between",
              slotProps?.buttonProps?.className,
            )}
          >
            {selected?.label ?? (
              <span className="text-placeholder">{placeholder ?? "Select..."}</span>
            )}
            <ChevronsUpDownIcon className="ms-2 size-4 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>

        <PopoverContent
          {...slotProps?.popoverContent}
          className={cn(
            "max-h(--radix-popover-content-available-height) w-(--radix-popover-trigger-width) p-0",
            slotProps?.popoverContent?.className,
          )}
        >
          <Command shouldFilter={!onSearch}>
            <CommandInput placeholder={searchPlaceholder ?? "Search"} onValueChange={onSearch} />
            <CommandList>
              {!loading && (
                <CommandEmpty>
                  {noOptionsMessage ?? (
                    <p className="text-foreground-secondary">No options available.</p>
                  )}
                </CommandEmpty>
              )}
              {loading && (
                <CommandLoading>
                  <div className="flex items-center justify-center py-6 text-center text-sm">
                    <Loader2 className="me-2 h-4 w-4 animate-spin" />
                    Loading...
                  </div>
                </CommandLoading>
              )}
              <CommandGroup>
                {mappedOptions.map((option) => (
                  <CommandItem
                    key={`${option.key}`}
                    value={option.key}
                    onSelect={(optionKey) => {
                      const option = getOptionByKey(optionKey);
                      if (isNullable && selected?.key === optionKey) {
                        onChange?.(null as TValue);
                        return;
                      }

                      onChange?.(option ?? null);
                      setOpen(false);
                    }}
                  >
                    <CheckIcon
                      className={cn(
                        "mr-2 h-4 w-4",
                        selected?.key === option.key ? "opacity-100" : "opacity-0",
                      )}
                    />
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {isNullable && value !== null && value !== undefined && (
        <Button
          type="button"
          size="icon"
          variant="light"
          color="foreground"
          onClick={() => onChange?.(null as TValue)}
          className="absolute start-[calc(100%+8px)] top-1/2 -translate-y-1/2 px-1"
        >
          <XIcon />
        </Button>
      )}
    </div>
  );
};
